import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { RejaPendientes, armaBloque, cambiaCeldaBloques, llenaBloques, lineasCapturadas } from "./RejaPendientes";
import { hoyLocal, enDiasLocal } from "./fechaLocal";

// Embarca desde la orden de compra, sin ir a otra pantalla: trae lo que falta
// por embarcar de esa orden (por color y talla), se captura lo que sube al
// contenedor y en una peticion se crea el embarque, su nota de cuentas por
// pagar y se descuenta lo embarcado de la orden.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);



function EmbarcarOCModal({ abierto, cerrar, orden, user, onEmbarcado }) {
  const [bloques, setBloques] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [forwarders, setForwarders] = useState([]);
  const [contenedor, setContenedor] = useState("");
  const [broker, setBroker] = useState("");
  const [forwarder, setForwarder] = useState("");
  const [fechaEmbarque, setFechaEmbarque] = useState(hoyLocal());
  const [fechaEstimada, setFechaEstimada] = useState(enDiasLocal(45));
  const [noFactura, setNoFactura] = useState("NA");
  const [bultos, setBultos] = useState(0);
  const [observaciones, setObservaciones] = useState("NA");
  // cuantos yuanes vale un dolar (solo si la orden es en CNY)
  const [tcCNY, setTcCNY] = useState(7.2);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto || !orden) return;
    setBloques([]); setContenedor(""); setGuardando(false);
    axios.get(process.env.REACT_APP_URL_BROKERS, { headers: headers() })
      .then((r) => setBrokers((r.data || []).filter((x) => x.is_active === "Si"))).catch(() => {});
    axios.get(process.env.REACT_APP_URL_FORWARDERS, { headers: headers() })
      .then((r) => setForwarders((r.data || []).filter((x) => x.is_active === "Si"))).catch(() => {});
    axios.get(`${process.env.REACT_APP_URL_ARTICULOS_OC}/ordenesCompra/${orden._id}`, { headers: headers() })
      .then((r) => {
        const lineas = (r.data || [])
          .filter((a) => Number(a.pendiente_embarcar) > 0 && a.articulos?.[0])
          .map((a) => ({
            _id: a._id,
            articulo: a.articulos[0],
            color: a.colores?.[0] || null,
            talla: a.tallas?.[0] || null,
            pendiente: Number(a.pendiente_embarcar) || 0,
            precio: Number(a.precio) || 0,
            moneda: a.ordenesCompra?.[0]?.moneda || orden.moneda || "USD",
            ordenesCompra: a.ordenesCompra?.[0]?._id || a.ordenesCompra?.[0] || orden._id,
            proveedores: a.ordenesCompra?.[0]?.proveedores?.[0]?._id || a.ordenesCompra?.[0]?.proveedores?.[0] || orden.idProveedores,
          }));
        setBloques(lineas.length
          ? [armaBloque(String(orden._id), `Orden ${orden.numero || orden.idOrdenCompra}`, orden.proveedores || "", lineas)]
          : []);
      })
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo traer la orden", text: e.message }));
  }, [abierto, orden]); // eslint-disable-line

  const capturadas = lineasCapturadas(bloques);
  // Orden en yuanes: el costo en dolares sale del tipo de cambio CNY por USD
  const enYuanes = bloques.some((b) => b.articulos.some((a) => Object.values(a.celdas).some((c) => c.lineas.some((l) => l.moneda === "CNY"))));
  const costoUSD = (l) => (l.moneda === "CNY" ? l.precio / (Number(tcCNY) || 1) : l.precio);
  const piezas = capturadas.reduce((s, c) => s + c.cantidad, 0);

  async function embarcar() {
    if (!contenedor.trim()) { Swal.fire({ icon: "warning", title: "Falta el contenedor", text: "Escribe el número de contenedor." }); return; }
    if (!capturadas.length) { Swal.fire({ icon: "warning", title: "Sin cantidades", text: "Captura lo que se embarca." }); return; }
    const ok = await Swal.fire({
      icon: "question", title: `Embarcar ${num(piezas)} piezas`,
      html: `Contenedor <b>${contenedor}</b> · ${capturadas.length} renglón(es).<br>Se crea el embarque y su nota de cuentas por pagar.`,
      showCancelButton: true, confirmButtonText: "Sí, embarcar", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const r = await axios.post(process.env.REACT_APP_URL_EMBARQUES_DESDE_ORDEN, {
        fechaEmbarque, fechaEstimada, contenedor: contenedor.trim(), observaciones,
        brokers: broker || null, forwarders: forwarder || null,
        noFactura, bultos: Number(bultos) || 0, tipoCambioUSDMXN: 19, tipoCambioCNYUSD: enYuanes ? Number(tcCNY) || 1 : 7.2, user,
        lineas: capturadas.map((c) => ({
          articulosOC: c.linea._id, ordenesCompra: c.linea.ordenesCompra,
          articulos: c.linea.articulo?._id,
          colores: c.linea.color?._id || null, tallas: c.linea.talla?._id || null,
          // costo en la moneda de la orden; costoUSD convertido (antes iba igual aunque la orden fuera en CNY)
          cantidad: c.cantidad, costo: c.linea.precio, costoUSD: costoUSD(c.linea),
          proveedores: c.linea.proveedores,
        })),
      }, { headers: headers() });
      setGuardando(false);
      await Swal.fire({
        icon: "success", title: `Embarque ${r.data.idEmbarques} creado`,
        html: `${num(r.data.total_cantidad)} piezas en ${r.data.renglones} renglón(es).<br>Se recibe después desde el listado de embarques.`,
      });
      cerrar();
      if (onEmbarcado) onEmbarcado();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo embarcar", text: error.response?.data?.message || error.message });
    }
  }

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        Embarcar orden {orden?.numero || orden?.idOrdenCompra} {orden?.proveedores ? `· ${orden.proveedores}` : ""}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={3}>
            <Label>Contenedor</Label>
            <Input bsSize="sm" value={contenedor} onChange={(e) => { const v = e.target.value; setContenedor(v); }} />
          </Col>
          <Col md={2}>
            <Label>Fecha de embarque</Label>
            <Input bsSize="sm" type="date" value={fechaEmbarque} onChange={(e) => { const v = e.target.value; setFechaEmbarque(v); }} />
          </Col>
          <Col md={2}>
            <Label>Llegada estimada</Label>
            <Input bsSize="sm" type="date" value={fechaEstimada} onChange={(e) => { const v = e.target.value; setFechaEstimada(v); }} />
          </Col>
          <Col md={2}>
            <Label>Agente aduanal</Label>
            <Input bsSize="sm" type="select" value={broker} onChange={(e) => { const v = e.target.value; setBroker(v); }}>
              <option value="">NA</option>
              {brokers.map((b) => <option key={b._id} value={b._id}>{b.name || b.nombre_comercial}</option>)}
            </Input>
          </Col>
          <Col md={2}>
            <Label>Forwarder</Label>
            <Input bsSize="sm" type="select" value={forwarder} onChange={(e) => { const v = e.target.value; setForwarder(v); }}>
              <option value="">NA</option>
              {forwarders.map((f) => <option key={f._id} value={f._id}>{f.name || f.nombre_comercial}</option>)}
            </Input>
          </Col>
          <Col md={1}>
            <Label>Bultos</Label>
            <Input bsSize="sm" type="number" value={bultos} onChange={(e) => { const v = e.target.value; setBultos(v); }} />
          </Col>
        </Row>
        {enYuanes ? (
          <Row className="mb-3">
            <Col md={3}>
              <Label>Tipo de cambio (CNY por 1 USD)</Label>
              <Input bsSize="sm" type="number" min="0" step="any" value={tcCNY}
                title="La orden esta en yuanes: con esto se calcula el costo en dolares"
                onChange={(e) => { const v = e.target.value; setTcCNY(v); }} />
            </Col>
            <Col md={9} style={{ display: "flex", alignItems: "flex-end", color: "#64748b", fontSize: 12 }}>
              La orden está en yuanes (CNY): el costo en dólares de cada pieza es el precio entre este tipo de cambio.
            </Col>
          </Row>
        ) : null}
        <RejaPendientes
          bloques={bloques}
          etiquetaPendiente="Falta"
          onCambia={(b, a, c, t, v) => setBloques((prev) => cambiaCeldaBloques(prev, b, a, c, t, v))}
          onLlenar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, true, c))}
          onLimpiar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, false, c))}
          onQuitar={() => {}}
        />
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          {capturadas.length} renglón(es) · <b>{num(piezas)}</b> piezas
        </Label>
        <Button color="success" onClick={embarcar} disabled={guardando || !piezas}>
          {guardando ? "Embarcando..." : "Embarcar"}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default EmbarcarOCModal;
